import { ONLY_NUMBER_REGEX, SINGLE_DIGIT_REGEX } from "@/constants/regex";

import { twMerge } from "tailwind-merge";

type FieldCoverageProps = {
  /** Current value, or null when empty. */
  value?: number | null;
  /** Called with the parsed value, or null when the field is cleared. */
  onChange: (value: number | null) => void;
  placeHolder: string;
  /** Minimum accepted value (inclusive). Values below it are clamped up. */
  min?: number;
  /** Maximum accepted value (inclusive). Values above it are clamped down. */
  max?: number;
  testId?: string;
  className?: string;
};

/**
 * Accepts integers within the [min, max] range. The lower bound is driven by
 * `min`, so pass `min={0}` to allow zero (default `min={1}` rejects it). Built
 * to be reused across coverage types (e.g. catchment by radius / by road
 * access) by swapping placeholder and range.
 */
const FieldCoverage = ({
  value,
  placeHolder,
  min = 0,
  max = 100000,
  testId = "coverage-field",
  className,
  onChange
}: FieldCoverageProps) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (ONLY_NUMBER_REGEX.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Once the field is a lone "0", block further digits so the value can't
    // grow into a leading-zero string (e.g. "00", "000", "05").
    const isDigit = SINGLE_DIGIT_REGEX.test(e.key);
    if (isDigit && e.currentTarget.value === "0") e.preventDefault();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    // Empty input clears the value.
    if (raw === "") {
      onChange(null);
      return;
    }

    // Reject leading zeros (e.g. "0", "00", "0000") so the field can't keep
    // accepting zeros once the value is already 0.
    if (raw.length > 1 && raw.startsWith("0")) return;

    // Reject non-numeric values and anything below the allowed minimum
    // (pass min={0} to allow zero).
    const parsed = Number(raw);
    if (Number.isNaN(parsed) || parsed < min) return;

    // Clamp into the [min, max] range.
    const clamped = Math.min(Math.max(Math.floor(parsed), min), max);
    onChange(clamped);
  };

  return (
    <div
      className={twMerge(
        "flex w-full items-center px-3 h-[40px] border rounded-[12px] overflow-hidden border-gainsboro focus-within:border-teal-500",
        className
      )}
    >
      <input
        className="w-full input-el"
        type="number"
        min={min}
        max={max}
        placeholder={placeHolder}
        value={value ?? ""}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        data-testid={testId}
      />
    </div>
  );
};

export default FieldCoverage;
